import {
  HttpClient as BaseHttpClient,
  HttpErrorResponse,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ServerConfigService } from '@app/configuration/server-config.service';
import { HttpStatusCodes } from '@app/http-status-codes';
import { WINDOW } from '@lib/browser/window-providers';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { RequestOptions } from './request-options';
import { RequestOptionsArguments } from './request-options-arguments';

@Injectable({ providedIn: 'root' })
export class HttpClient {
  private unexpectedErrors: HttpStatusCodes[] = [
    HttpStatusCodes.REQUEST_TIMEOUT,
    HttpStatusCodes.INTERNAL_SERVER_ERROR,
    HttpStatusCodes.BAD_GATEWAY,
    HttpStatusCodes.SERVICE_UNAVAILABLE,
    HttpStatusCodes.GATEWAY_TIMEOUT,
  ];

  constructor(
    private configService: ServerConfigService,
    private httpClient: BaseHttpClient,
    @Inject(WINDOW) private windowRef: Window,
  ) {}

  get<T>(
    path: string,
    options: RequestOptionsArguments = new RequestOptionsArguments(),
  ): Observable<T> {
    return this.httpClient
      .get<T>(this.buildUrl(path), new RequestOptions(options))
      .pipe(catchError(this.handleError));
  }

  post<T, Body>(
    path: string,
    body: Body,
    options: RequestOptionsArguments = new RequestOptionsArguments(),
  ): Observable<T> {
    return this.httpClient
      .post<T>(this.buildUrl(path), body, new RequestOptions(options))
      .pipe(catchError(this.handleError));
  }

  patch<T, Body>(
    path: string,
    body: Body,
    options: RequestOptionsArguments = new RequestOptionsArguments(),
  ): Observable<T> {
    return this.httpClient
      .patch<T>(this.buildUrl(path), body, new RequestOptions(options))
      .pipe(catchError(this.handleError));
  }

  get baseUrl(): string {
    return this.configService.config.apiBaseUrl;
  }

  private buildUrl(path: string) {
    return this.baseUrl + path;
  }

  private handleError = async (error: HttpErrorResponse) => {
    if (this.unexpectedErrors.includes(error.status)) {
      await this.navigateToErrorPage();
    }

    throw new Error(error.message);
  };

  private navigateToErrorPage = async () => {
    // Using browser redirect instead of angular routing to force
    // browser to reload if the maintenance page is up.
    this.windowRef.location.href = '/error';
  };
}
